import { memo, useEffect, useState } from "react";
import { Handle, Position } from "reactflow";
import "./nodeStyles.css";
import UploadFile from "../../assets/icons/AddFile.svg";
import { Image } from "react-bootstrap";
import { Button, Space, Tooltip, Upload } from "antd";
import { useFileUploadsMutation } from "../../service/apiSlices/dashboard";
import removeFile from "../../assets/icons/remove_file.svg";
import replaceFile from "../../assets/icons/replace_file.svg";
import { uploadFile } from "../../utilities/utils";
import { UploadNodeProps } from "../../utilities/types/common";
import { uploadNodeConfig } from "../../utilities/Constants/Constants";
import { useTranslation } from "react-i18next";
import { showErrorToast, showSuccessToast } from "../../utilities/toastUtils";
import { usePermission } from "../../hooks/usePermission";

const UploadNode = ({ data, id }: UploadNodeProps) => {
  const { t } = useTranslation('common');
  const [filePath, setFilePath] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [forceUpdate, setForceUpdate] = useState(0);
  const [uploadFileMutation] = useFileUploadsMutation();
  const { canView, canEdit } = usePermission();

  useEffect(() => {
    if (data?.content) {
      setFilePath(data.content);
      setFileName(data.content.split("/").pop() || "");
    } else {
      setFilePath("");
      setFileName("");
    }
  }, [data?.content]);

  const handleUploadChange = async (info: any) => {
    const { file } = info;

    try {
      await uploadFile(
        file,
        uploadFileMutation,
        uploadNodeConfig,
        (uploadedData) => {
          setFilePath(uploadedData);
          setFileName(file.name);
          if (typeof data.onContentUpdate === "function") {
            data.onContentUpdate(id, uploadedData);
          }
        },
        (error) => {
          console.error("Upload error:", error);
        }
      );
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  const handleRemoveFile = () => {
    setFilePath("");
    setFileName("");
    setForceUpdate((prev) => prev + 1);

    if (typeof data.onContentUpdate === "function") {
      data.onContentUpdate(id, "");
      showSuccessToast("File removed successfully");
    }
  };

  const renderFilePreview = () => {
    if (!filePath) return null;

    const isImage = /\.(jpg|jpeg|png)$/i.test(filePath);

    return isImage ? (
      <Image
        key={`${filePath}-${forceUpdate}`}
        src={filePath}
        alt="Uploaded file preview"
        className="uploaded-image"
        onError={(e: any) => {
          e.target.onerror = null;
          showErrorToast("Failed to load image");
        }}
      />
    ) : (
      <div className="non-image-file-preview">
        <a
          href={filePath}
          target="_blank"
          rel="noopener noreferrer"
          className="file-link"
        >
          {fileName.split('_').slice(1).join('_')}
        </a>
      </div>
    );
  };

  return (
    <div className="node upload-node">
      <div className="node-header">
        <div className="card border-0 w-100 node-inner-card bg-transparent">
          <div className="card-body">
            <div className="content d-flex justify-content-center align-items-center">
              {filePath ? (
                <div className="file-container" key={forceUpdate}>
                  <div className="file-controls">
                    <Upload
                      maxCount={1}
                      customRequest={() => { }}
                      beforeUpload={() => false}
                      onChange={handleUploadChange}
                      showUploadList={false}
                      className="workbench-file-upload"
                      accept={uploadNodeConfig.allowedExtensions.join(',')}
                    >
                      {canEdit && canView && (
                        <Tooltip title="Replace file">
                          <Button type="link" className="border-0 replace-button">
                            <img
                              src={replaceFile}
                              alt="Replace file"
                              width={20}
                            />
                          </Button>
                        </Tooltip>
                      )}
                    </Upload>
                    {canEdit && canView && (
                      <Tooltip title="Remove file">
                        <Button
                          type="link"
                          className="remove-button"
                          onClick={handleRemoveFile}
                        >
                          <img src={removeFile} alt="Remove file" width={20} />
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                  <div className="file-preview">{renderFilePreview()}</div>
                </div>
              ) : (
                <Space
                  direction="vertical"
                  className="upload-wrapper"
                  size="large"
                >
                  {canEdit && canView && (
                    <Upload
                      maxCount={1}
                      customRequest={() => { }}
                      beforeUpload={() => false}
                      showUploadList={false}
                      onChange={handleUploadChange}
                      className="workbench-file-upload"
                      accept={uploadNodeConfig.allowedExtensions.join(',')}
                    >
                      <Button className="border-0 upload-button">
                        <Image src={UploadFile} alt="upload" />
                      </Button>
                    </Upload>
                  )}
                </Space>
              )}
            </div>
          </div>
        </div>
        <p className="mb-0 upload-content">
          {t("workbench.uploadNode.uploadContent")}
        </p>
      </div>
      <Handle type="target" position={Position.Left} className="handle" />
    </div>
  );
};

export default memo(UploadNode);